import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconAlertTriangle, IconExternalLink, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react"
import { EXPIRY_CLASS, type ExpiryLevel, STATUS_BADGE, expiryLabel } from "./status"

interface LegalDocument {
  id: number
  title: string
  document_type: string
  document_type_label: string
  status: string
  customer_id: number
  customer_name: string
  effective_date: string | null
  end_date: string | null
  expiry_level: ExpiryLevel
  days_until_expiry: number | null
  document_url: string | null
  can_update: boolean
  can_destroy: boolean
}

interface Option {
  value: string
  label: string
}

interface CustomerOption {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  documents: LegalDocument[]
  pagy: PagyMeta
  status_counts: Record<string, number>
  document_types: Option[]
  statuses: Option[]
  customers: CustomerOption[]
  filters: Record<string, string>
  can_create: boolean
}

const ANY = "all"

function ExpiryCell({ document }: { document: LegalDocument }) {
  const label = expiryLabel(document.expiry_level, document.days_until_expiry)

  if (!label) {
    return <span className="text-muted-foreground">{formatDate(document.end_date)}</span>
  }

  return (
    <span className={`inline-flex items-center gap-1.5 font-medium ${EXPIRY_CLASS[document.expiry_level || ""]}`}>
      <IconAlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {formatDate(document.end_date)}
      <span className="text-xs font-normal">({label})</span>
    </span>
  )
}

export default function LegalDocumentsIndex() {
  const { documents, pagy, document_types, statuses, customers, filters, can_create } = usePage<Props>().props

  function applyFilter(key: string, value: string) {
    const next = { ...filters, [key]: value === ANY ? "" : value }
    router.get("/legal_documents", next, { preserveState: true, replace: true })
  }

  function handleDelete(document: LegalDocument) {
    if (confirm(`Delete "${document.title}"? This cannot be undone.`)) {
      router.delete(`/legal_documents/${document.id}`)
    }
  }

  return (
    <>
      <Head title="Documents - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Documents</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Legal Documents</h1>
          {can_create && (
            <Button size="sm" asChild>
              <a href="/legal_documents/new">
                <IconPlus className="mr-1 h-4 w-4" />
                New Document
              </a>
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Search title or customer"
            defaultValue={filters.search || ""}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilter("search", e.currentTarget.value)
            }}
            className="max-w-xs"
          />

          <Select value={filters.document_type || ANY} onValueChange={(v) => applyFilter("document_type", v)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All types</SelectItem>
              {document_types.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.status || ANY} onValueChange={(v) => applyFilter("status", v)}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All statuses</SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.customer_id || ANY} onValueChange={(v) => applyFilter("customer_id", v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All customers</SelectItem>
              {customers.map((customer) => (
                <SelectItem key={customer.id} value={String(customer.id)}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-xl border bg-card">
          {documents.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Effective</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((document) => {
                    const badge = STATUS_BADGE[document.status] || {
                      variant: "outline" as const,
                      label: document.status,
                    }

                    return (
                      <TableRow key={document.id}>
                        <TableCell>
                          <a
                            href={`/legal_documents/${document.id}`}
                            className="text-primary font-medium hover:underline"
                          >
                            {document.title}
                          </a>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{document.document_type_label}</Badge>
                        </TableCell>
                        <TableCell>
                          <a href={`/customers/${document.customer_id}`} className="hover:underline">
                            {document.customer_name}
                          </a>
                        </TableCell>
                        <TableCell>
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm tabular-nums">
                          {formatDate(document.effective_date)}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">
                          <ExpiryCell document={document} />
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {document.document_url && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                                <a
                                  href={document.document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Open ${document.title}`}
                                >
                                  <IconExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                            {document.can_update && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                                <a href={`/legal_documents/${document.id}/edit`} aria-label={`Edit ${document.title}`}>
                                  <IconPencil className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                            {document.can_destroy && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label={`Delete ${document.title}`}
                                onClick={() => handleDelete(document)}
                              >
                                <IconTrash className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-muted-foreground py-12 text-center text-sm">No documents found</p>
          )}
        </div>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

LegalDocumentsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
